import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, Twitter, Linkedin, Instagram } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const translations = {
  es: {
    solutions: {
      title: 'Soluciones',
      items: {
        productManager: 'Product Manager',
        consultant: 'Consultor',
        developer: 'Programador',
        projectManager: 'Project Manager',
        entrepreneur: 'Emprendedor'
      }
    },
    support: {
      title: 'Soporte',
      items: {
        pricing: 'Precios',
        documentation: 'Documentación',
        guides: 'Guías',
        apiStatus: 'Estado de la API'
      }
    },
    company: {
      title: 'Empresa',
      items: {
        about: 'Sobre Nosotros',
        blog: 'Blog',
        jobs: 'Empleo',
        press: 'Prensa',
        partners: 'Socios'
      }
    },
    legal: {
      title: 'Legal',
      items: {
        privacy: 'Privacidad',
        terms: 'Términos'
      }
    },
    copyright: '© 2025 Cortex AI. Todos los derechos reservados.'
  },
  en: {
    solutions: {
      title: 'Solutions',
      items: {
        productManager: 'Product Manager',
        consultant: 'Consultant',
        developer: 'Developer',
        projectManager: 'Project Manager',
        entrepreneur: 'Entrepreneur'
      }
    },
    support: {
      title: 'Support',
      items: {
        pricing: 'Pricing',
        documentation: 'Documentation',
        guides: 'Guides',
        apiStatus: 'API Status'
      }
    },
    company: {
      title: 'Company',
      items: {
        about: 'About Us',
        blog: 'Blog',
        jobs: 'Jobs',
        press: 'Press',
        partners: 'Partners'
      }
    },
    legal: {
      title: 'Legal',
      items: {
        privacy: 'Privacy',
        terms: 'Terms'
      }
    },
    copyright: '© 2025 Cortex AI. All rights reserved.'
  }
};

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <footer className="bg-secondary border-t border-border">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="space-y-8 xl:col-span-1">
            <div className="flex items-center">
              <Brain className="h-8 w-8 text-accent" />
              <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">Cortex AI</span>
            </div>
            <p className="text-foreground/80 text-base">
              {language === 'es' 
                ? 'Tu compañero de aprendizaje profesional impulsado por IA. Mentoría personalizada y simulaciones del mundo real.'
                : 'Your AI-powered professional learning companion. Personalized mentoring and real-world simulations.'}
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-foreground/60 hover:text-accent transition-colors">
                <span className="sr-only">Twitter</span>
                <Twitter className="h-6 w-6" />
              </a>
              <a href="#" className="text-foreground/60 hover:text-accent transition-colors">
                <span className="sr-only">LinkedIn</span>
                <Linkedin className="h-6 w-6" />
              </a>
              <a href="#" className="text-foreground/60 hover:text-accent transition-colors">
                <span className="sr-only">Instagram</span>
                <Instagram className="h-6 w-6" />
              </a>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold text-foreground/60 tracking-wider uppercase">{t.solutions.title}</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.solutions.items.productManager}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.solutions.items.consultant}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.solutions.items.developer}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.solutions.items.projectManager}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.solutions.items.entrepreneur}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-semibold text-foreground/60 tracking-wider uppercase">{t.support.title}</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.support.items.pricing}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.support.items.documentation}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.support.items.guides}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.support.items.apiStatus}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold text-foreground/60 tracking-wider uppercase">{t.company.title}</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link to="/nosotros" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.company.items.about}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.company.items.blog}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.company.items.jobs}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.company.items.press}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.company.items.partners}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="mt-12 md:mt-0">
                <h3 className="text-sm font-semibold text-foreground/60 tracking-wider uppercase">{t.legal.title}</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.legal.items.privacy}
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="text-base text-foreground/80 hover:text-accent transition-colors">
                      {t.legal.items.terms}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 border-t border-border pt-8">
          <p className="text-base text-foreground/60 xl:text-center">
            {t.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;